/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'

import {Box, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useContentfulPageBySlug} from '../../clients/contentful/hooks'
import CategoryGrid from '../../components/category-grid'
import HeroSection from '../../components/hero-section'
import HeroImageSlider from '../../components/hero-section/hero-carousel'
import ServiceCard from '../../components/home-service-section/service-card'
import ProductScroller from '../../components/product-scroller'
import VideoSection from '../../components/video-section'
import {SectionTypes} from '../../constants'
import {contentfulMediaFormatter} from '../../utils/contentful'
import IngredientGrid from './ingredient-grid'
import StructuredData from './schema/structured-data'
import Seo from '../../components/seo'

const ProductSection = ({fields}) => {
    const carouselProducts = (fields?.items ?? []).map((item) => {
        const asset = contentfulMediaFormatter(item?.fields?.image)
        return {
            ...item.fields,
            image: asset,
            brand: item?.fields?.brand?.fields ?? null
        }
    })

    return (
        <ProductScroller
            productCarouselContent={{
                carouselCollection: {
                    items: [
                        {
                            heading: fields?.heading,
                            cta: fields?.cta?.fields,
                            itemsCollection: {
                                items: carouselProducts
                            }
                        }
                    ]
                }
            }}
            isLoading={false}
            px={{base: '16px', lg: '32px', xl: '43px'}}
            showScrollerNav
            maxWidth={{
                base: 'calc(100vw - 40px)',
                lg: 'calc(100vw - 32px)'
            }}
            itemWidth={{
                base: 'calc(50% - 6.5px)',
                md: 'calc(33.33% - 8.66px)',
                lg: 'calc(25% - 9.75px)',
                xl: 'calc(25% - 9.75px)'
            }}
            cardsGap="13px"
            scrollContainerId="product-scroller-container"
        />
    )
}

const ServiceCardSection = ({fields: {items = []}}) => (
    <>
        {items?.map(({fields}) => {
            const asset = contentfulMediaFormatter(fields?.image)

            return (
                <ServiceCard
                    serviceCard={{
                        ...fields,
                        cta: fields?.cta?.fields,
                        image: asset
                    }}
                    variant="service"
                />
            )
        })}
    </>
)

const IngredientGlossarySection = ({fields}) => {
    const items = (fields?.items ?? []).map(({fields}) => {
        const asset = contentfulMediaFormatter(fields?.media)
        return {
            ...fields,
            media: asset,
            cta: fields?.cta?.fields
        }
    })

    return (
        <IngredientGrid
            data={{
                itemsCollection: {items}
            }}
        />
    )
}

const DefaultContentGridSection = ({fields}) => (
    <>
        {fields?.items?.map(({fields}) => {
            const asset = contentfulMediaFormatter(fields?.image)
            const item = {
                ...fields,
                cta: fields?.cta?.fields,
                image: asset
            }

            return <ServiceCard serviceCard={item} variant="service" />
        })}
    </>
)

const Section = ({dataSection}) => {
    const {sys, fields} = dataSection
    const sectionType = sys?.contentType?.sys?.id

    const itemscomponentMapping = {
        homeHeroSection: <HeroImageSlider carousel={fields} />,
        product: <ProductSection fields={fields} />,
        serviceCard: <ServiceCardSection fields={fields} />,
        'Category Grid': <CategoryGrid data={fields} />,
        'Ingredient Glossary': <IngredientGlossarySection fields={fields} />,
        defaultContentGrid: <DefaultContentGridSection fields={fields} />
    }

    switch (sectionType) {
        case SectionTypes.carousel:
            const internalSectionTypes = fields?.items?.map(
                (field) => field?.sys?.contentType?.sys?.id
            )
            const internalSectionType = Array.isArray(internalSectionTypes)
                ? internalSectionTypes.at(-1)
                : internalSectionTypes

            if (internalSectionType)
                return itemscomponentMapping[internalSectionType]

            return <></>
        case SectionTypes.homeHeroSection:
            return (
                <HeroSection data={fields} marginX={{base: '17px', md: '0'}} />
            )
        case SectionTypes.textAsset:
            const asset = contentfulMediaFormatter(fields?.media)
            return (
                <VideoSection
                    videoSectionData={{
                        ...fields,
                        media: asset
                    }}
                    borderRadius="20px"
                    py={{base: '20px', md: 'none'}}
                />
            )
        case SectionTypes.contentGrid:
            const contentGridSectionType = fields?.sectionType
            if (contentGridSectionType)
                return itemscomponentMapping[contentGridSectionType]

            return itemscomponentMapping.defaultContentGrid
        default:
            return <></>
    }
}

/**
 * This is the home page for Retail React App.
 * The page is created for demonstration purposes.
 * The page renders SEO metadata and a few promotion
 * categories and products, data is from local file.
 */

const Home = () => {
    const {data} = useContentfulPageBySlug('home')

    return (
        <Box
            data-testid="home-page"
            layerStyle="homePage"
            gap={{base: '30px', lg: '80px'}}
            width="100%"
        >
            <StructuredData />
            <Seo
                title="Rennaï: Your Self-Care & Beauty Boutique in Montréal, Canada"
                description="Rennaï: Your Self-Care & Beauty Boutique in Montréal, Canada"
                keywords="Rennaï, MountRoyal, Canada, Montreal"
            />

            <Flex
                width="100%"
                flexFlow="column"
                gap={{base: '30px', lg: '80px'}}
            >
                {data?.sections?.map((section) => (
                    <Section dataSection={section} />
                ))}
            </Flex>
        </Box>
    )
}

Home.getTemplateName = () => 'home'

export default Home
